/**
 * CR096: Attribute Visibility Tab - Toggle visibility of deal attributes across surfaces
 * CR141: Added meaning + example tooltips per attribute
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Eye, Info, HelpCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { getAttributeHelp } from "@/lib/config/attributeHelpDictionary";

interface AttributeRule {
  attribute_key: string;
  show_in_staging: boolean;
  show_in_admin_prod: boolean;
  show_in_public: boolean;
  updated_by: string | null;
  updated_at: string;
}

interface AttributeVisibilityTabProps {
  rules: AttributeRule[];
  loading: boolean;
  onRefresh: () => void;
  onUpdate: (rule: AttributeRule) => void;
}

export function AttributeVisibilityTab({ rules, loading, onRefresh, onUpdate }: AttributeVisibilityTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingRows, setUpdatingRows] = useState<Set<string>>(new Set());

  const updateVisibility = async (
    attributeKey: string,
    field: "show_in_staging" | "show_in_admin_prod" | "show_in_public",
    value: boolean
  ) => {
    // Optimistic update
    const current = rules.find((r) => r.attribute_key === attributeKey);
    if (current) {
      onUpdate({ ...current, [field]: value });
    }
    setUpdatingRows((prev) => new Set([...prev, attributeKey]));

    try {
      const { data, error } = await supabase.functions.invoke("command-center-attributes", {
        method: "PUT",
        body: {
          attribute_key: attributeKey,
          [field]: value,
        },
      });

      if (error) {
        console.error("[AttributeVisibilityTab] Update error:", error);
        toast.error(`Failed to update ${attributeKey}`);
        onRefresh();
        return;
      }

      if (data.rule) {
        onUpdate(data.rule);
      }

      toast.success(`Updated ${attributeKey} visibility`);
    } catch (err) {
      console.error("[AttributeVisibilityTab] Unexpected error:", err);
      toast.error(`Failed to update ${attributeKey}`);
      onRefresh();
    } finally {
      setUpdatingRows((prev) => {
        const next = new Set(prev);
        next.delete(attributeKey);
        return next;
      });
    }
  };

  // Search matches on key, label, or meaning
  const filteredRules = rules.filter((r) => {
    const help = getAttributeHelp(r.attribute_key);
    const query = searchQuery.toLowerCase();
    return (
      r.attribute_key.toLowerCase().includes(query) ||
      help.label.toLowerCase().includes(query) ||
      help.meaning.toLowerCase().includes(query)
    );
  });

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        {/* Info Banner */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Control which deal attributes are visible on each surface.
            <Badge variant="secondary" className="mx-1">Staging</Badge> is for internal review,
            <Badge variant="secondary" className="mx-1">Admin (Prod)</Badge> is the operator view,
            <Badge variant="secondary" className="mx-1">Public</Badge> is customer-facing.
            <span className="block mt-1 text-xs text-muted-foreground">
              Hover over the <HelpCircle className="inline h-3 w-3" /> icon to see what each attribute means.
            </span>
          </AlertDescription>
        </Alert>

        {/* Visibility Rules Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Attribute Visibility Matrix
            </CardTitle>
            <CardDescription>
              Toggle visibility for each attribute across different surfaces.
              Changes take effect immediately.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by attribute, label, or meaning..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Table */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[280px]">Attribute</TableHead>
                    <TableHead className="text-center">Staging</TableHead>
                    <TableHead className="text-center">Admin (Prod)</TableHead>
                    <TableHead className="text-center">Public</TableHead>
                    <TableHead className="text-right">Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        {searchQuery ? "No attributes match your search" : "No attributes found"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRules.map((rule) => {
                      const isUpdating = updatingRows.has(rule.attribute_key);
                      const help = getAttributeHelp(rule.attribute_key);
                      return (
                        <TableRow key={rule.attribute_key}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="text-muted-foreground hover:text-foreground transition-colors">
                                    <HelpCircle className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="right" 
                                  className="max-w-xs p-3 bg-popover border shadow-lg z-50"
                                  sideOffset={8}
                                >
                                  <div className="space-y-2">
                                    <p className="font-medium text-sm">{help.label}</p>
                                    <p className="text-xs text-muted-foreground">{help.meaning}</p>
                                    <div className="pt-1 border-t">
                                      <span className="text-xs text-muted-foreground">Example: </span>
                                      <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">
                                        {help.example}
                                      </code>
                                    </div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">{help.label}</span>
                                <Badge variant="outline" className="font-mono text-[10px] w-fit mt-0.5">
                                  {rule.attribute_key}
                                </Badge>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={rule.show_in_staging}
                              onCheckedChange={(v) =>
                                updateVisibility(rule.attribute_key, "show_in_staging", v)
                              }
                              disabled={isUpdating}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={rule.show_in_admin_prod}
                              onCheckedChange={(v) =>
                                updateVisibility(rule.attribute_key, "show_in_admin_prod", v)
                              }
                              disabled={isUpdating}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={rule.show_in_public}
                              onCheckedChange={(v) =>
                                updateVisibility(rule.attribute_key, "show_in_public", v)
                              }
                              disabled={isUpdating}
                            />
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {rule.updated_by && (
                              <span className="block text-xs truncate max-w-[150px]">
                                {rule.updated_by}
                              </span>
                            )}
                            <span className="text-xs">
                              {formatDistanceToNow(new Date(rule.updated_at), { addSuffix: true })}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
